/* eslint unused-imports/no-unused-imports: "off" */
import React, {useEffect, useRef, useState} from "react"
import {formatTextTestIds} from "@next/utils"
import Fade from "@mui/material/Fade"
import FacetsPriceSelector from "../../facetsPriceSelector"
import connect from "./connect"
import env from "../../../config/env"
import FacetList from "./facetList"
import FacetLabel from "./facetLabel"
import FacetListLabelWrapper from "./facetListLabelWrapper"
import FilterSearchBar from "./filterSearchBar"
import {Wrapper, PopperWrapper, LabelButton, ViewAllButton, FilterWrapper} from "./components"
import {FacetState} from "../../../models/FacetsState"
import {Filter} from "../../../models/Filter"
import {TextModel} from "../../../models/Text"
import FacetListClearAllButton from "./facetListClearAllButton"
import FacetListSelectLabel from "./facetListSelectLabel"
import ChevronImage from "./chevronImage"
import ViewAllWrapper from "./viewAllWrapper"

export interface FacetListBoxProps {
    filterName: string
    facets: FacetState[]
    handleSetFacet: (facetValue: string) => void
    clearTypeFilters: (displayName: string, filterName: string) => void
    filter: Filter
    numberOfFacetsSelected: number
    openViewAllModal: (filterName: string) => void
    showViewAllButton: boolean
    text: TextModel
    isOnSecondRow?: boolean
    updateNumberOfFacetsLoaded: (filterName: string) => void
    lazyLoadFacets: boolean
    showBorderAfterTopFive: boolean
    siteUrl: string
    popularFacetCount?: number
    sortFacetsAlphabetically?: boolean
    compositeFilter: boolean
    setFreeTextSearchForFilter: (e: React.SyntheticEvent, filterName: string) => void
    freeTextFilterSearch: {[key: string]: string}
    clearFreeTextSearchForFilter: (filterName: string) => void
    facetBoxMinWidth: number
    freeTextFiltersList: string[]
    anyOptionSelected: boolean
}

export const FacetListBox = ({
    filterName,
    facets,
    handleSetFacet,
    clearTypeFilters,
    filter,
    numberOfFacetsSelected,
    openViewAllModal,
    showViewAllButton,
    text,
    isOnSecondRow,
    updateNumberOfFacetsLoaded,
    lazyLoadFacets,
    showBorderAfterTopFive,
    siteUrl,
    popularFacetCount = 0,
    sortFacetsAlphabetically = false,
    setFreeTextSearchForFilter,
    freeTextFilterSearch,
    clearFreeTextSearchForFilter,
    facetBoxMinWidth,
    freeTextFiltersList,
    compositeFilter,
    anyOptionSelected,
}: FacetListBoxProps) => {
    // console.log(showViewAllButton, filterName)
    const anchorElementRef = useRef(null)
    const [filterOpen, setFilterOpen] = useState(false)
    const [isEnterKeyDown, setIsEnterKeyDown] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const popperRef = useRef<HTMLDivElement>(null)
    const getTestIdForElement = elementName => formatTextTestIds(`plp-filter-${elementName}-${filterName}`)

    const handleClickOutside = event => {
        if (
            !wrapperRef?.current?.contains(event.target) &&
            !popperRef?.current?.contains(event.target) &&
            !["option"].includes(event.target.role)
        ) {
            setFilterOpen(false)
        }
    }

    const handleScroll = e => {
        setFilterOpen(false)
    }

    const removeEventListeners = () => {
        document.removeEventListener("mousedown", handleClickOutside)
        document.removeEventListener("scroll", handleScroll)
        document.removeEventListener("focusin", handleClickOutside)
    }

    useEffect(() => {
        return () => {
            removeEventListeners()
        }
    }, [])

    useEffect(() => {
        if (filterOpen) {
            document.addEventListener("mousedown", handleClickOutside)
            document.addEventListener("scroll", handleScroll)
            document.addEventListener("focusin", handleClickOutside)
        } else {
            removeEventListeners()
        }
    }, [filterOpen])

    const openFilterOnClick = () => {
        setIsEnterKeyDown(false)
        setFilterOpen(!filterOpen)
    }

    const openFilterEnterKeyDown = event => {
        if (event.key === "Enter") {
            setIsEnterKeyDown(true)
            setFilterOpen(!filterOpen)
            event.preventDefault()
        }
    }

    return (
        <Wrapper ref={wrapperRef}>
            <FilterWrapper
                $isFilterSelected={filterOpen}
                onClick={openFilterOnClick}
                ref={anchorElementRef}
                $anyOptionSelected={anyOptionSelected}
                data-testid={getTestIdForElement("label-wrapper")}
            >
                <LabelButton
                    variant="contained"
                    onClick={openFilterOnClick}
                    $isFilterSelected={filterOpen}
                    disableRipple
                    disableFocusRipple
                    data-testid={getTestIdForElement("label-button")}
                    onKeyDown={e => openFilterEnterKeyDown(e)}
                >
                    <FacetLabel $isOnSecondRow={isOnSecondRow} filterName={filterName} filter={filter} />
                </LabelButton>
                <ChevronImage
                    $isFilterOpen={filterOpen}
                    src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/chevron.svg`}
                    alt="chevron"
                    dataTestId={getTestIdForElement("chevron")}
                />
            </FilterWrapper>
            <PopperWrapper
                ref={popperRef}
                open={filterOpen}
                anchorEl={anchorElementRef?.current}
                placement="bottom-start"
                data-testid={getTestIdForElement("list-wrapper")}
                $facetBoxMinWidth={facetBoxMinWidth}
                modifiers={[
                    {
                        name: "flip",
                        options: {
                            flipVariations: false,
                            fallbackPlacements: [],
                            allowedAutoPlacements: [],
                        },
                    },
                    {
                        name: "offset",
                        options: {
                            offset: [-1, 3],
                        },
                    },
                ]}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
            >
                <Fade in={filterOpen}>
                    {filter.type === "price" ? (
                        <div>
                            <FacetsPriceSelector name={filter.name} />
                        </div>
                    ) : (
                        <div>
                            <FacetListLabelWrapper>
                                <>
                                    {numberOfFacetsSelected === 0 ? (
                                        <FacetListSelectLabel>
                                            <span>Select {filter.displayName}</span>
                                        </FacetListSelectLabel>
                                    ) : (
                                        <FacetListSelectLabel childtestid={getTestIdForElement("selected-facet-count")}>
                                            <span>{numberOfFacetsSelected} selected</span>
                                        </FacetListSelectLabel>
                                    )}
                                    {numberOfFacetsSelected > 0 && (
                                        <FacetListClearAllButton
                                            filter={filter}
                                            clearTypeFilters={clearTypeFilters}
                                            filterOpen={filterOpen}
                                            text={text}
                                            filterName={filterName}
                                        />
                                    )}
                                </>
                            </FacetListLabelWrapper>
                            {(freeTextFiltersList.includes(filterName) || compositeFilter) && (
                                <FilterSearchBar
                                    freeTextFilterSearch={freeTextFilterSearch}
                                    clearFreeTextSearchForFilter={() => clearFreeTextSearchForFilter(filterName)}
                                    setFreeTextSearchForFilter={e => setFreeTextSearchForFilter(e, filterName)}
                                    text={text}
                                    filterName={filterName}
                                    filterDisplayName={filter.displayName}
                                />
                            )}
                            <FacetList
                                handleSetFacet={handleSetFacet}
                                filterName={filterName}
                                facets={facets}
                                isEnterKeyDown={isEnterKeyDown}
                                lazyLoadFacets={lazyLoadFacets}
                                updateNumberOfFacetsLoaded={updateNumberOfFacetsLoaded}
                                showBorderAfterTopFive={showBorderAfterTopFive}
                                siteUrl={siteUrl}
                                data-testid={`plp-filter-list-${filterName}`}
                                popularFacetCount={popularFacetCount}
                                text={text}
                                sortFacetsAlphabetically={sortFacetsAlphabetically}
                                compositeFilter={compositeFilter}
                            />
                            {showViewAllButton && (
                                <ViewAllWrapper dataTestId={getTestIdForElement("view-all-wrapper")}>
                                    <ViewAllButton
                                        onClick={() => openViewAllModal(filterName)}
                                        data-testid={getTestIdForElement("view-all-button")}
                                        disableRipple
                                        disableFocusRipple
                                    >
                                        {text.buttons.viewAll}
                                    </ViewAllButton>
                                </ViewAllWrapper>
                            )}
                        </div>
                    )}
                </Fade>
            </PopperWrapper>
        </Wrapper>
    )
}

export default connect(FacetListBox)
